import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PayPalSubscription from './PayPalSubscription';
import SubscriptionModal from './SubscriptionModal';
import TrialModal from './TrialModal';
import ErrorBoundary from './ErrorBoundary';
import './SubscriptionSection.css';

const SubscriptionCard = ({ planKey, planData, isPopular, index, onModalOpen, onTrialModalOpen }) => {
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = React.useState('annual'); // 默认选择年付

  const calculateSavings = (monthly, yearly) => {
    const monthlyTotal = parseFloat(monthly.replace('$', '')) * 12;
    const yearlyPrice = parseFloat(yearly.replace('$', ''));
    const savings = monthlyTotal - yearlyPrice;
    const percentage = Math.round((savings / monthlyTotal) * 100);
    return {
      savings: savings.toFixed(2),
      percentage,
      originalPrice: `$${monthlyTotal.toFixed(0)}`,
      monthlyTotal
    };
  };

  if (planKey === 'website') {
    return (
      <div className={`subscription-card website-card fade-in-up`} style={{ animationDelay: `${index * 0.2}s` }}>
        <div className="temp-badge">
          {t('subscription.website.tempLabel')}
        </div>
        
        <div className="card-header">
          <h3 className="plan-title">{t(`subscription.${planKey}.title`)}</h3>
          <div className="website-price-section">
            <div className="website-price-display">
              <span className="price">{planData.monthlyPrice}</span>
              <span className="period">{t('subscription.monthlyPeriod')}</span>
            </div>
            <div className="website-note">
              <p>{t('subscription.website.billingNote')}</p>
            </div>
          </div>
        </div>

        <div className="card-content">
          <div className="benefits-list">
            <ul>
              {t(`subscription.${planKey}.features`, { returnObjects: true }).map((feature, idx) => (
                <li key={idx}><span className="check-icon">✓</span>{feature}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="card-footer website-footer">
          <div className="website-payment-options">
            <div className="paypal-section">
              <PayPalSubscription 
                planId="P-1RC72271P9352551ANDDV4WQ"
                onSuccess={(subscriptionID) => {
                  alert(`网站服务订阅成功！订阅ID: ${subscriptionID}`);
                }}
                onError={(error) => {
                  console.error('订阅失败:', error);
                  alert('订阅过程中发生错误，请重试或联系客服。');
                }}
              />
            </div>
            <div className="alternative-payment">
              <p className="payment-note">{t('subscription.website.paymentNote')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (planKey === 'trial') {
    return (
      <div className={`subscription-card trial-card fade-in-up`} style={{ animationDelay: `${index * 0.2}s` }}>
        <div className="card-header">
          <h3 className="plan-title">{t(`subscription.${planKey}.title`)}</h3>
          <div className="trial-price-section">
            <div className="trial-price-display">
              <span className="price">{planData.price}</span>
              <span className="period">{t('subscription.trial.period')}</span>
            </div>
          </div>
        </div>

        <div className="card-content">
          <div className="benefits-list">
            <ul>
              {t(`subscription.${planKey}.features`, { returnObjects: true }).map((feature, idx) => (
                <li key={idx}><span className="check-icon">✓</span>{feature}</li>
              ))}
            </ul>
          </div>
          <div className="trial-note">
            <p>{t('subscription.trial.note')}</p>
          </div>
        </div>

        <div className="card-footer trial-footer">
          <button 
            onClick={() => onTrialModalOpen && onTrialModalOpen(planData)}
            className="cta-button trial"
          >
            {t(`subscription.${planKey}.cta`)}
          </button>
        </div>
      </div>
    );
  }

  const savingsData = calculateSavings(planData.monthlyPrice, planData.yearlyPrice);

  return (
    <div className={`subscription-card ${isPopular ? 'popular' : ''} fade-in-up`} style={{ animationDelay: `${index * 0.2}s` }}>
      {isPopular && (
        <div className="popular-badge">
          {t('subscription.mostPopular')}
        </div>
      )}
      
      <div className="best-value-corner">
        <span>{t('subscription.bestValue')}</span>
      </div>

      <div className="card-header">
        <h3 className="plan-title">{t(`subscription.${planKey}.title`)}</h3>
        
        <div className="pricing-options">
          <div
            className={`pricing-option monthly ${selectedPlan === 'monthly' ? 'selected' : ''}`}
            onClick={() => setSelectedPlan('monthly')}
          >
            <div className="plan-selector">
              <div className={`radio-button ${selectedPlan === 'monthly' ? 'selected' : ''}`}>
                {selectedPlan === 'monthly' && <div className="radio-dot"></div>}
              </div>
            </div>
            <div className="pricing-content">
              <div className="price-label">{t('subscription.monthlyOption')}</div>
              <div className="price-display">
                <span className="price">{planData.monthlyPrice}</span>
              </div>
            </div>
          </div>

          <div
            className={`pricing-option annual ${selectedPlan === 'annual' ? 'selected' : ''}`}
            onClick={() => setSelectedPlan('annual')}
          >
            <div className="plan-selector">
              <div className={`radio-button ${selectedPlan === 'annual' ? 'selected' : ''}`}>
                {selectedPlan === 'annual' && <div className="radio-dot"></div>}
              </div>
            </div>
            <div className="pricing-content">
              <div className="price-label">{t('subscription.annualOption')}</div>
              <div className="annual-price-display">
                <div className="price-comparison">
                  <span className="original-price">{savingsData.originalPrice}</span>
                  <span className="current-price">{planData.yearlyPrice}</span>
                </div>
              </div>
              <div className="savings-highlight">
                <span className="savings-amount">省${savingsData.savings}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card-content">
        <div className="benefits-list">
          <ul>
            {t(`subscription.${planKey}.features`, { returnObjects: true }).map((feature, idx) => (
              <li key={idx}><span className="check-icon">✓</span>{feature}</li>
            ))}
          </ul>
        </div>

        {planKey === 'full' && (
          <div className="extra-benefits">
            <div className="benefits-header">{t('subscription.bonusFeatures')}</div>
            <ul>
              {t('subscription.full.benefits', { returnObjects: true }).map((benefit, idx) => (
                <li key={idx}><span className="star-icon">★</span>{benefit}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="card-footer">
        <button
          onClick={() => onModalOpen(planKey, selectedPlan)}
          className={`cta-button ${isPopular ? 'primary' : 'primary-outline'} single-cta`}
        >
          {selectedPlan === 'monthly' ? t('subscription.subscribeMonthly') : t('subscription.subscribeAnnual')}
        </button>
      </div>
    </div>
  );
};

const SubscriptionSection = () => {
  const { t } = useTranslation();
  const [modalState, setModalState] = useState({
    isOpen: false,
    subscriptionType: null,
    billingPeriod: null,
    planData: null
  });
  
  const [trialModalState, setTrialModalState] = useState({
    isOpen: false,
    planData: null
  });

  const handleModalOpen = (planKey, billingPeriod) => {
    const planData = subscriptionPlans.find(plan => plan.key === planKey);
    
    // 只有找到有效的planData才打开modal
    if (!planData) {
      console.error('Plan data not found for key:', planKey);
      return;
    }

    // 确保试用和网站服务卡片不会触发modal
    if (planKey === 'trial' || planKey === 'website') {
      return;
    }

    setModalState({
      isOpen: true,
      subscriptionType: planKey,
      billingPeriod: billingPeriod,
      planData: planData
    });
  };

  const handleModalClose = () => {
    setModalState({
      isOpen: false,
      subscriptionType: null,
      billingPeriod: null,
      planData: null
    });
  };

  const handleTrialModalOpen = (planData) => {
    setTrialModalState({
      isOpen: true,
      planData: planData
    });
  };

  const handleTrialModalClose = () => {
    setTrialModalState({
      isOpen: false,
      planData: null
    });
  };

  const subscriptionPlans = [
    {
      key: 'single',
      monthlyPrice: '$19.90',
      yearlyPrice: '$119',
      ctaUrl: '#subscribe-single'
    },
    {
      key: 'full',
      monthlyPrice: '$39.90',
      yearlyPrice: '$399',
      ctaUrl: '#subscribe-full',
      isPopular: true
    },
    {
      key: 'trial',
      price: '$4.99',
      ctaUrl: '#subscribe-trial'
    },
    {
      key: 'website',
      monthlyPrice: '$35',
      ctaUrl: '#subscribe-website'
    }
  ];

  return (
    <section id="subscription" className="subscription-section section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title fade-in-up">{t('subscription.title')}</h2>
          <p className="section-subtitle fade-in-up">
            {t('subscription.description')}
          </p>
        </div>
        
        <div className="subscription-grid">
          {subscriptionPlans.map((plan, index) => (
            <SubscriptionCard
              key={plan.key}
              planKey={plan.key}
              planData={plan}
              isPopular={plan.isPopular}
              index={index}
              onModalOpen={handleModalOpen}
              onTrialModalOpen={handleTrialModalOpen}
            />
          ))}
        </div>

        <ErrorBoundary>
          <SubscriptionModal
            isOpen={modalState.isOpen}
            onClose={handleModalClose}
            subscriptionType={modalState.subscriptionType}
            billingPeriod={modalState.billingPeriod}
            planData={modalState.planData}
          />
        </ErrorBoundary>

        <ErrorBoundary>
          <TrialModal
            isOpen={trialModalState.isOpen}
            onClose={handleTrialModalClose}
            planData={trialModalState.planData}
          />
        </ErrorBoundary>

        <div className="subscription-note fade-in-up">
          <p>{t('subscription.note')}</p>
        </div>
      </div>
    </section>
  );
};

export default SubscriptionSection;